<template>
  <BButton
    :variant="buttonVariant"
    :size="size"
    :disabled="disabled"
    @click="handleClick"
  >
    <slot />
  </BButton>
</template>

<script setup>
import { computed } from 'vue'

/**
 * BaseButton - 基本ボタンコンポーネント (Atom)
 *
 * アプリケーション全体で使用される再利用可能な基本ボタンです。
 * Bootstrap Vueのb-buttonコンポーネントをラップして、
 * アプリケーション固有のデザインルールを適用します。
 *
 * 責務:
 * - UIの描画に専念（ロジックは持たない）
 * - propsでデータを受け取る
 * - $emitで親に操作を通知する
 */

const props = defineProps({
  /**
   * ボタンの見た目を指定します
   * 'primary': 主要な操作で使用（例：保存、OK、登録）
   * 'secondary': 補助的な操作で使用（例：キャンセル、戻る）
   * 'success': 成功・完了系の操作（例：承認、完了）
   * 'danger': 危険・削除系の操作（例：削除、クリア）
   * 'warning': 注意・警告系の操作（例：一時保存、警告確認）
   */
  variant: {
    type: String,
    default: 'primary',
    validator: (value) => {
      return ['primary', 'secondary', 'success', 'danger', 'warning'].includes(value)
    }
  },
  /**
   * ボタンのサイズを指定します
   * 'sm': 小サイズ（テーブル内ボタンなど）
   * 'md': 標準サイズ（一般的なボタン）
   * 'lg': 大サイズ（重要なアクションボタン）
   */
  size: {
    type: String,
    default: 'md',
    validator: (value) => {
      return ['sm', 'md', 'lg'].includes(value)
    }
  },
  /**
   * ボタンを無効化するかどうかを指定します
   */
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['click'])

/**
 * Bootstrap Vueのバリアントに変換します
 */
const buttonVariant = computed(() => {
  const variantMap = {
    primary: 'primary',
    secondary: 'outline-secondary',
    success: 'success',
    danger: 'danger',
    warning: 'warning'
  }
  return variantMap[props.variant] || 'primary'
})

/**
 * クリックイベントを親コンポーネントに通知します
 * @param {Event} event - クリックイベント
 */
const handleClick = (event) => {
  if (!props.disabled) {
    /**
     * @event click
     * @type {Event} クリックイベント
     */
    emit('click', event)
  }
}
</script>

<style scoped>
/* 必要に応じてアプリケーション固有のスタイルを追加 */
</style>
